import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Card,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Link
} from '@material-ui/core';

import CardContent from '../../../common/card/CardContent';
import StatusLabel from '../../../common/StatusLabel';
import { useGetStatusLogs, getStatusLabel } from '../Workflow.hooks';
import { WorkflowDto, WorkflowStatusLog } from '../Workflow.interface';
import { formatDate } from '../../../common/formatter';
import UserLink from '../../users/components/UserLink';

const StyledTypography = styled(Typography)`
  font-weight: 500;
`;

export interface WorkflowStatusCardProps {
  workflow: WorkflowDto;
}

const WorkflowStatusCard: React.FC<WorkflowStatusCardProps> = ({
  workflow
}) => {
  const { workflowStatusLogs: delta, isLoading, get } = useGetStatusLogs(
    workflow.id
  );
  const limit = 5;
  const [page, setPage] = useState(0);
  const workflowRef = useRef(workflow);
  const [workflowStatusLogs, setWorkflowStatusLogs] = useState<
    WorkflowStatusLog[]
  >([]);
  const statusLabel = workflow.status && getStatusLabel(workflow.status);

  useEffect(() => {
    if (workflow !== workflowRef.current) {
      setWorkflowStatusLogs([]);
      workflowRef.current = workflow;
      get({ page: 0, limit });
    } else {
      get({ page, limit });
    }
  }, [get, page, workflow]);

  useEffect(() => {
    if (delta && delta.data) {
      const logs = [
        ...workflowStatusLogs,
        ...delta.data.filter(
          (newLog: any) =>
            // don't insert log if it already exists; occurs when partial page load
            !workflowStatusLogs.find(log => log.id === newLog.id)
        )
      ];
      logs.sort(
        // resort the list after appending statuses
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
      setWorkflowStatusLogs(logs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delta]);

  return (
    <Card>
      <CardContent background='primary'>
        <Typography
          variant='overline'
          style={{ margin: 0, paddingTop: '20px' }}
        >
          Status
        </Typography>
        <StyledTypography variant='h4'>{statusLabel}</StyledTypography>
      </CardContent>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>History</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Employee</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={4} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {workflowStatusLogs &&
              workflowStatusLogs.map((statusLog, index) => (
                <TableRow key={index}>
                  <TableCell>{formatDate(statusLog.created)}</TableCell>
                  <TableCell>{statusLog.version}</TableCell>
                  <TableCell>
                    <StatusLabel status={statusLog.status} />
                  </TableCell>
                  <TableCell>
                    <UserLink
                      type='name'
                      disableLink={true}
                      user={statusLog.createdBy}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {delta && delta.total > (page + 1) * limit && (
        <Box textAlign='center' padding={2}>
          <Link
            component='button'
            variant='body2'
            onClick={() => setPage(page + 1)}
          >
            Load More
          </Link>
        </Box>
      )}
    </Card>
  );
};

export default WorkflowStatusCard;
